import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Card as CardMaterial,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  createTheme,
  CardMedia,
  Tooltip,
  Box,
  Typography,
  InputLabel,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteNoticia,
  getNoticiaById,
  postNoticia,
  updateNoticia,
} from "../../../../api/noticia";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { Autocomplete } from "@material-ui/lab";
import { getCategorias } from "../../../../api/categoria";
import { Delete, FlashOn, Visibility } from "@material-ui/icons";
import { SERVER_URL } from "../../../../api";
import { buttonsStyle } from "../../../components/tables/table";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import Editor from "../../../components/editor/Editor";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import { PictureAsPdf, Description, InsertDriveFile } from "@material-ui/icons";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import MultiTabBlock from "../../../components/MultiTabBlock";
import { getNonEmpty } from "../../../../utils/helpers";
import ImageStorageSelectorDialog from "../../../components/dialogs/ImageStorageSelectorDialog";
import { shallowEqual, useSelector } from "react-redux";
import { WEB_URL } from "../../../../constants/utils.constants";

// Create theme for delete button (red)
const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

const normalizeBaseUrl = (url) => (url || "").replace(/\/+$/, "");

const getNoticiaBasePathByLang = (lang) => {
  switch ((lang || "").toLowerCase()) {
    case "es":
      return "/es/blog";
    case "en":
      return "/en/blog";
    case "ca":
    default:
      return "/blog";
  }
};

const buildPublicUrl = ({ basePath, slug }) => {
  const base = normalizeBaseUrl(WEB_URL);
  const path = `${basePath || ""}/${slug || ""}`.replace(/\/+$/, "");
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
};

function getEmptyNoticia() {
  return {
    subtitulo: {},
    titulo: {},
    imageURL: "",
    imageGridURL: "",
    fileURL: "",
    youtube: "",
    fuente: "",
    summary: {},
    content: {},
    link: {},
    urlNoticia: {},
    active: true,
    destacado: false,
    fechaPublicacion: null,
  };
}

export default function EditNoticiaPage() {
  const [noticia, setNoticia] = useState(getEmptyNoticia());
  const [categorias, setCategorias] = useState([]); // Todas las categorías
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageGrid, setSelectedImageGrid] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(null);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openImageSelector, setOpenImageSelector] = useState(false);
  const [openFileSelector, setOpenFileSelector] = useState(false);
  const [openImageGridSelector, setOpenImageGridSelector] = useState(false);
  const noticiaId = useParams().id;
  const history = useHistory();
  const languages = useSelector(
    (store) => store.authentication?.languages,
    shallowEqual,
  );

  const imageInputRef = useRef(null);
  const imageGridInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resCategorias = await getCategorias();
        if (resCategorias.status === 200) {
          setCategorias(resCategorias.data);
        }
        if (!noticiaId) {
          disableLoadingData();
          return;
        }
        getNoticiaById(noticiaId)
          .then((res) => {
            if (res.status === 200) {
              const noticiaData = res.data;

              // Convertir fechaPublicacion a formato para input de fecha
              if (noticiaData.fechaPublicacion) {
                noticiaData.fechaPublicacion = new Date(
                  noticiaData.fechaPublicacion,
                )
                  .toISOString()
                  .split("T")[0];
              }

              setNoticia(noticiaData);

              // Manejar categoria: puede venir como objeto populado o como string ID
              const categoriaId =
                typeof res.data.cate === "object" && res.data.cate?._id
                  ? res.data.cate._id.toString()
                  : res.data.cate?.toString();

              const categoriaEncontrada = resCategorias.data.find(
                (x) => x._id.toString() === categoriaId,
              );

              if (categoriaEncontrada) {
                setSelectedCategoria(categoriaEncontrada);
              }

              disableLoadingData();
            }
          })
          .catch((error) => {
            alertError({
              error: error,
              customMessage: "Could not get noticia.",
            });
            history.push("/noticias");
          });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [noticiaId, disableLoadingData, history]);

  function saveNoticia() {
    // Validaciones
    if (!selectedCategoria) {
      alertError({
        customMessage: "Debe seleccionar una categoría.",
      });
      return;
    }

    // Crear una copia limpia del objeto noticia
    const { _id, __v, createdAt, updatedAt, ...noticiaToSave } = noticia;
    let saveNoticia = { ...noticiaToSave };
    saveNoticia.cate = selectedCategoria._id;

    // Convertir fechaPublicacion de string a Date si existe
    if (saveNoticia.fechaPublicacion) {
      saveNoticia.fechaPublicacion = new Date(saveNoticia.fechaPublicacion);
    } else {
      // Si no hay fechaPublicacion, establecer null explícitamente
      saveNoticia.fechaPublicacion = null;
    }

    if (!noticiaId) {
      postNoticia(saveNoticia, selectedImage, selectedImageGrid, selectedFile)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Noticia successfully created.",
            });
            history.push("/noticias");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save noticia.",
          });
        });
    } else {
      updateNoticia(
        noticiaId,
        saveNoticia,
        selectedImage,
        selectedImageGrid,
        selectedFile,
      )
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/noticias");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  // Función para manejar el cambio de categoría
  const handleCategoriaChange = (event, selected) => {
    setSelectedCategoria(selected);
  };

  const handleChange = (element, lang) => (event) => {
    if (lang) {
      if (event.target.value === " ") return;
      if (!noticia[element]) noticia[element] = {};
      let newText = noticia[element];
      newText[lang] = event.target.value;
      setNoticia({ ...noticia, [element]: newText });
    } else setNoticia({ ...noticia, [element]: event.target.value });
  };

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleChangeEditor = (element, lang, value) => {
    if (lang) {
      if (value === " ") return;
      if (!noticia[element]) noticia[element] = {};
      let newText = noticia[element];
      newText[lang] = value;
      setNoticia({ ...noticia, [element]: newText });
    } else setNoticia({ ...noticia, [element]: value });
  };

  const renderMultilanguageTabContent = (lang) => {
    return (
      <>
        <TextField
          id={`titulo`}
          label="Titulo"
          value={(noticia.titulo && noticia.titulo[lang]) || ""}
          onChange={handleChange("titulo", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
        <TextField
          id={`subtitulo`}
          label="Subtitulo"
          value={(noticia.subtitulo && noticia.subtitulo[lang]) || ""}
          onChange={handleChange("subtitulo", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
        <TextField
          id={`urlNoticia`}
          label="URL Externa"
          value={(noticia.urlNoticia && noticia.urlNoticia[lang]) || ""}
          onChange={handleChange("urlNoticia", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
        />
        <br />
        <label>Cabecera</label>
        <Editor
          body={(noticia.header && noticia.header[lang]) || ""}
          setBody={(new_body) => handleChangeEditor("header", lang, new_body)}
          className="max-height"
          lang={lang}
          placeholder={"Enter news cabecera here..."}
        />
        <br />
        <label>Resumen</label>
        <Editor
          body={(noticia.summary && noticia.summary[lang]) || ""}
          setBody={(new_body) => handleChangeEditor("summary", lang, new_body)}
          className="max-height"
          lang={lang}
          placeholder={"Enter news resumen here..."}
        />
        <br />
        <label>Contenido</label>
        <Editor
          body={(noticia.content && noticia.content[lang]) || ""}
          setBody={(new_body) => handleChangeEditor("content", lang, new_body)}
          className="max-height"
          lang={lang}
          placeholder={"Enter news content here..."}
        />
      </>
    );
  };

  // Función para obtener el icono según el tipo de archivo
  const getFileIcon = (fileName) => {
    if (!fileName) return <InsertDriveFile />;

    const extension = fileName
      .split(".")
      .pop()
      ?.toLowerCase();

    switch (extension) {
      case "pdf":
        return <PictureAsPdf style={{ fontSize: 60, color: "#d32f2f" }} />;
      case "doc":
      case "docx":
        return <Description style={{ fontSize: 60, color: "#1976d2" }} />;
      case "xls":
      case "xlsx":
        return <Description style={{ fontSize: 60, color: "#388e3c" }} />;
      case "ppt":
      case "pptx":
        return <Description style={{ fontSize: 60, color: "#f57c00" }} />;
      case "txt":
        return <Description style={{ fontSize: 60, color: "#616161" }} />;
      default:
        return <InsertDriveFile style={{ fontSize: 60, color: "#757575" }} />;
    }
  };

  // Función para obtener el nombre del archivo
  const getFileName = (file, fileURL) => {
    if (file) {
      return file.name;
    }
    if (fileURL) {
      return fileURL.split("/").pop() || "Archivo";
    }
    return "Archivo";
  };

  const stripHTML = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    let text = tmp.textContent || tmp.innerText || "";
    text = text.replace(/\s+/g, " ").trim();
    return text;
  };

  const generateSlug = (text) => {
    if (!text) return "";
    let slug = stripHTML(text);
    slug = slug.toLowerCase();
    slug = slug
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    return slug;
  };

  const renderContentDatosGenerales = () => {
    return (
      <>
        <div className="d-flex">
          <div className="col-3">
            <TextField
              id={`fuente`}
              label="Fuente"
              value={noticia.fuente || ""}
              onChange={handleChange("fuente")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
            />
          </div>
          <div className="col-3">
            <Autocomplete
              className="w-100 mr-5"
              id="autocomplete-categoria"
              filterSelectedOptions
              options={categorias}
              getOptionLabel={(option) =>
                getNonEmpty(option?.titulo) === "----"
                  ? ""
                  : getNonEmpty(option?.titulo)
              }
              value={selectedCategoria}
              onChange={handleCategoriaChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Seleccionar categoría"
                  required
                />
              )}
            />
          </div>
          <div className="col-3">
            <TextField
              id={`youtube`}
              label="Código video youtube"
              value={noticia.youtube || ""}
              onChange={handleChange("youtube")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
            />
          </div>
        </div>
        <div className="d-flex">
          <div className="col-md-3">
            <TextField
              id={`fechaPublicacion`}
              label="Fecha de Publicación"
              type="date"
              value={noticia.fechaPublicacion || ""}
              onChange={handleChange("fechaPublicacion")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              helperText="Dejar vacío para usar la fecha de creación. La noticia aparecerá en la web a partir de esta fecha."
            />
          </div>
        </div>
        <div className="d-flex">
          <div className="col-md-6 mt-2">
            <InputLabel>
              Imagen Cartel 1200x675<sub>(16:9)</sub>
            </InputLabel>
            <CardMaterial>
              {(selectedImage || noticia.imageURL) && (
                <CardMedia
                  component="img"
                  alt="New"
                  style={{
                    objectFit: "contain",
                    width: "100%",
                    height: "auto",
                    maxHeight: "600px",
                  }}
                  image={
                    selectedImage
                      ? URL.createObjectURL(selectedImage)
                      : `${SERVER_URL}/${noticia.imageURL}`
                  }
                />
              )}
            </CardMaterial>
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "16px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <label htmlFor={"upload-media"} className="ml-0">
                <input
                  ref={imageInputRef}
                  style={{ display: "none" }}
                  id={"upload-media"}
                  name={"upload-media"}
                  type="file"
                  accept={"image/*"}
                  onChange={(e) => {
                    setSelectedImage(e.target.files[0]);
                  }}
                />
                <Button
                  style={{ marginRight: "15px" }}
                  color="secondary"
                  component="span"
                  variant="outlined"
                >
                  {selectedImage ||
                  (noticia.imageURL && noticia.imageURL !== "")
                    ? "cambiar imagen"
                    : "subir imagen"}
                </Button>
              </label>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenImageSelector(true)}
              >
                Seleccionar de galería
              </Button>
            </div>
            {(selectedImage ||
              (noticia.imageURL && noticia.imageURL !== "")) && (
              <>
                <Tooltip title={"Preview"}>
                  <Button
                    size="small"
                    onClick={() => setOpenPreviewDialog(1)}
                    style={{
                      ...buttonsStyle,
                      marginRight: "15px",
                    }}
                  >
                    <Visibility />
                  </Button>
                </Tooltip>
                <PreviewDialog
                  title={"Preview"}
                  open={openPreviewDialog === 1}
                  setOpen={setOpenPreviewDialog}
                  file={selectedImage}
                  src={
                    selectedImage
                      ? URL.createObjectURL(selectedImage)
                      : `${SERVER_URL}/${noticia.imageURL}`
                  }
                />
                <Tooltip title={"Delete"}>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedImage(null);
                      setNoticia({
                        ...noticia,
                        imageURL: null,
                      });
                      if (imageInputRef.current) {
                        imageInputRef.current.value = "";
                      }
                    }}
                  >
                    <Delete />
                  </Button>
                </Tooltip>
              </>
            )}
            <br />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: "8px" }}
            >
              Imagen para Grid 1200x675<sub>(16:9)</sub> (Opcional)
            </Typography>
            <CardMaterial>
              {(selectedImageGrid || noticia.imageGridURL) && (
                <CardMedia
                  component="img"
                  alt="Grid"
                  style={{
                    objectFit: "contain",
                    width: "100%",
                    height: "auto",
                    maxHeight: "300px",
                  }}
                  image={
                    selectedImageGrid
                      ? URL.createObjectURL(selectedImageGrid)
                      : `${SERVER_URL}/${noticia.imageGridURL}`
                  }
                />
              )}
            </CardMaterial>
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "16px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <label htmlFor={"upload-media-grid"} className="ml-0">
                <input
                  ref={imageGridInputRef}
                  style={{ display: "none" }}
                  id={"upload-media-grid"}
                  name={"upload-media-grid"}
                  type="file"
                  accept={"image/*"}
                  onChange={(e) => {
                    setSelectedImageGrid(e.target.files[0]);
                  }}
                />
                <Button
                  style={{ marginRight: "15px" }}
                  color="secondary"
                  component="span"
                  variant="outlined"
                >
                  {selectedImageGrid ||
                  (noticia.imageGridURL && noticia.imageGridURL !== "")
                    ? "cambiar imagen grid"
                    : "subir imagen grid"}
                </Button>
              </label>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenImageGridSelector(true)}
              >
                Seleccionar de galería
              </Button>
            </div>
            {(selectedImageGrid ||
              (noticia.imageGridURL && noticia.imageGridURL !== "")) && (
              <>
                <Tooltip title={"Preview"}>
                  <Button
                    size="small"
                    onClick={() => setOpenPreviewDialog(2)}
                    style={{
                      ...buttonsStyle,
                      marginRight: "15px",
                    }}
                  >
                    <Visibility />
                  </Button>
                </Tooltip>
                <PreviewDialog
                  title={"Preview Grid"}
                  open={openPreviewDialog === 2}
                  setOpen={setOpenPreviewDialog}
                  file={selectedImageGrid}
                  src={
                    selectedImageGrid
                      ? URL.createObjectURL(selectedImageGrid)
                      : `${SERVER_URL}/${noticia.imageGridURL}`
                  }
                />
                <Tooltip title={"Delete"}>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedImageGrid(null);
                      setNoticia({
                        ...noticia,
                        imageGridURL: null,
                      });
                      if (imageGridInputRef.current) {
                        imageGridInputRef.current.value = "";
                      }
                    }}
                  >
                    <Delete />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
          <div className="col-md-6">
            <CardMaterial>
              {(selectedFile || noticia.fileURL) && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: 3,
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    backgroundColor: "#f9f9f9",
                    minHeight: "150px",
                    justifyContent: "center",
                  }}
                >
                  {getFileIcon(getFileName(selectedFile, noticia.fileURL))}
                  <Typography
                    variant="body1"
                    sx={{
                      marginTop: 1,
                      textAlign: "center",
                      wordBreak: "break-word",
                      maxWidth: "100%",
                    }}
                  >
                    {getFileName(selectedFile, noticia.fileURL)}
                  </Typography>
                  {selectedFile && (
                    <Typography variant="caption" color="textSecondary">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  )}
                </Box>
              )}
            </CardMaterial>

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "16px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <label htmlFor={"upload-file"} className="ml-0">
                <input
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  id={"upload-file"}
                  name={"upload-file"}
                  type="file"
                  accept={".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Validar tamaño del archivo (ejemplo: máximo 10MB)
                      const maxSize = 10 * 1024 * 1024; // 10MB
                      if (file.size > maxSize) {
                        alert(
                          "El archivo es demasiado grande. El tamaño máximo permitido es 10MB.",
                        );
                        return;
                      }
                      setSelectedFile(file);
                    }
                  }}
                />
                <Button
                  style={{ marginRight: "15px" }}
                  color="secondary"
                  component="span"
                  variant="outlined"
                >
                  {selectedFile || (noticia.fileURL && noticia.fileURL !== "")
                    ? "Cambiar documento"
                    : "Subir documento"}
                </Button>
              </label>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenFileSelector(true)}
              >
                Seleccionar de galería
              </Button>
            </div>

            {(selectedFile || (noticia.fileURL && noticia.fileURL !== "")) && (
              <>
                <Tooltip title={"Vista previa"}>
                  <Button
                    size="small"
                    onClick={() => {
                      // Para documentos, abrir en nueva ventana o descargar
                      const url = selectedFile
                        ? URL.createObjectURL(selectedFile)
                        : `${SERVER_URL}/${noticia.fileURL}`;

                      if (
                        selectedFile &&
                        selectedFile.type === "application/pdf"
                      ) {
                        // Para PDFs, abrir en nueva ventana
                        window.open(url, "_blank");
                      } else if (
                        !selectedFile &&
                        noticia.fileURL &&
                        noticia.fileURL.endsWith(".pdf")
                      ) {
                        // Para PDFs del servidor, abrir en nueva ventana
                        window.open(url, "_blank");
                      } else {
                        // Para otros tipos de documento, descargar
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = getFileName(
                          selectedFile,
                          noticia.fileURL,
                        );
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                    style={{
                      ...buttonsStyle,
                      marginRight: "15px",
                    }}
                  >
                    <Visibility />
                  </Button>
                </Tooltip>

                <Tooltip title={"Eliminar"}>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedFile(null);
                      setNoticia({
                        ...noticia,
                        fileURL: null,
                      });
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                  >
                    <Delete />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
        </div>
        <br />
        <div className="col-12">
          <MultilanguageTabBlock
            multilanguageTabContent={renderMultilanguageTabContent}
          />
        </div>
      </>
    );
  };
  const renderMultilanguageTabContentEnlace = (lang) => {
    return (
      <>
        <TextField
          id={`link`}
          label="Slug (URL amigable de la noticia en la web)"
          value={noticia.link && noticia.link[lang] ? noticia.link[lang] : ""}
          onChange={handleChange("link", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: "-4px", marginBottom: "8px" }}
        >
          <span style={{ fontFamily: "monospace" }}>
            {buildPublicUrl({
              basePath: getNoticiaBasePathByLang(lang),
              slug: (noticia.link && noticia.link[lang]) || "",
            })}
          </span>
        </Typography>
        <TextField
          id={`tituloEnlace`}
          label="Titulo SEO"
          value={
            noticia.tituloEnlace && noticia.tituloEnlace[lang]
              ? noticia.tituloEnlace[lang]
              : ""
          }
          onChange={handleChange("tituloEnlace", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
        <TextField
          id={`decripcionEnlace`}
          label="Descripción SEO"
          value={
            noticia.decripcionEnlace && noticia.decripcionEnlace[lang]
              ? noticia.decripcionEnlace[lang]
              : ""
          }
          onChange={handleChange("decripcionEnlace", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
      </>
    );
  };

  const renderContentEnlace = () => {
    const handleGenerateSEO = async () => {
      if (!languages || languages.length === 0) {
        alertError({
          customMessage: "No hay idiomas disponibles para generar el SEO",
        });
        return;
      }

      const noticiaInfo = {
        titulo: noticia && noticia.titulo ? noticia.titulo : {},
        summary: noticia && noticia.summary ? noticia.summary : {},
        content: noticia && noticia.content ? noticia.content : {},
      };

      try {
        alertSuccess({
          title: "Generando SEO...",
          customMessage:
            "Por favor espera mientras se genera el SEO para todos los idiomas",
        });

        const seoData = {};

        for (const lang of languages) {
          const langCode = lang.isocode;
          if (!langCode) continue;

          const titulo =
            noticiaInfo.titulo && noticiaInfo.titulo[langCode]
              ? noticiaInfo.titulo[langCode]
              : noticiaInfo.titulo && noticiaInfo.titulo["es"]
              ? noticiaInfo.titulo["es"]
              : "";
          const summary =
            noticiaInfo.summary && noticiaInfo.summary[langCode]
              ? noticiaInfo.summary[langCode]
              : noticiaInfo.summary && noticiaInfo.summary["es"]
              ? noticiaInfo.summary["es"]
              : "";
          const content =
            noticiaInfo.content && noticiaInfo.content[langCode]
              ? noticiaInfo.content[langCode]
              : noticiaInfo.content && noticiaInfo.content["es"]
              ? noticiaInfo.content["es"]
              : "";

          const tituloLimpio = stripHTML(titulo);
          const summaryLimpio = stripHTML(summary);
          const contentLimpio = stripHTML(content);

          const tituloSEO =
            tituloLimpio && tituloLimpio.length > 0
              ? tituloLimpio.length > 60
                ? tituloLimpio.substring(0, 57) + "..."
                : tituloLimpio
              : "Noticia";

          let descripcionSEO = "";
          if (summaryLimpio && summaryLimpio.length > 0) {
            descripcionSEO =
              summaryLimpio.length > 160
                ? summaryLimpio.substring(0, 157) + "..."
                : summaryLimpio;
          } else if (contentLimpio && contentLimpio.length > 0) {
            descripcionSEO =
              contentLimpio.length > 160
                ? contentLimpio.substring(0, 157) + "..."
                : contentLimpio;
          }

          if (!descripcionSEO || descripcionSEO.length === 0) {
            descripcionSEO = `Información sobre la noticia ${tituloSEO}`;
          }

          const urlAmigable = generateSlug(tituloLimpio || tituloSEO);

          seoData[langCode] = {
            link: urlAmigable,
            tituloEnlace: tituloSEO,
            decripcionEnlace: descripcionSEO,
          };
        }

        setNoticia((prev) => ({
          ...prev,
          link: {
            ...prev.link,
            ...Object.fromEntries(
              Object.entries(seoData).map(([lang, data]) => [lang, data.link]),
            ),
          },
          tituloEnlace: {
            ...prev.tituloEnlace,
            ...Object.fromEntries(
              Object.entries(seoData).map(([lang, data]) => [
                lang,
                data.tituloEnlace,
              ]),
            ),
          },
          decripcionEnlace: {
            ...prev.decripcionEnlace,
            ...Object.fromEntries(
              Object.entries(seoData).map(([lang, data]) => [
                lang,
                data.decripcionEnlace,
              ]),
            ),
          },
        }));

        alertSuccess({
          title: "SEO generado!",
          customMessage: `Se ha generado el SEO para ${
            Object.keys(seoData).length
          } idiomas`,
        });
      } catch (error) {
        console.error("Error generando SEO:", error);
        alertError({
          error: error,
          customMessage: "No se ha podido generar el SEO",
        });
      }
    };

    return (
      <>
        <div className="d-flex justify-content-end mb-3">
          <Button
            variant="contained"
            onClick={handleGenerateSEO}
            startIcon={<FlashOn />}
            style={{
              backgroundColor: "#9c27b0",
              color: "#fff",
            }}
          >
            GENERAR
          </Button>
        </div>
        <MultilanguageTabBlock
          multilanguageTabContent={renderMultilanguageTabContentEnlace}
        />
      </>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/noticias"),
            onSave: saveNoticia,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!noticiaId,
            setObj: setNoticia,
            obj: noticia,
          })}
          <CardHeader title="Editar Noticia"></CardHeader>
          <CardBody>
            <MultiTabBlock
              tabs={[
                {
                  enable: true,
                  title: "Datos Generales",
                  renderContent: renderContentDatosGenerales,
                },
                {
                  enable: true,
                  title: "SEO",
                  renderContent: renderContentEnlace,
                },
              ]}
            />

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noticia.active !== false}
                    onChange={() =>
                      setNoticia({ ...noticia, active: !noticia.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noticia.destacado === true}
                    onChange={() =>
                      setNoticia({ ...noticia, destacado: !noticia.destacado })
                    }
                    name="checkDestacado"
                    style={{ marginLeft: "20px" }}
                  />
                }
                label="Destacado"
              />
            </div>
          </CardBody>
        </Card>

        {/* Diálogos de selección de imageStorage */}
        <ImageStorageSelectorDialog
          open={openImageSelector}
          onClose={() => setOpenImageSelector(false)}
          onSelect={(url) => {
            // Asegurar que la URL sea relativa (sin SERVER_URL)
            const relativeUrl = url.startsWith(SERVER_URL)
              ? url.replace(SERVER_URL + "/", "")
              : url.replace(/^\//, ""); // Quitar barra inicial si existe
            setNoticia({ ...noticia, imageURL: relativeUrl });
            setSelectedImage(null);
            // Limpiar el input de archivo si existe
            if (imageInputRef.current) {
              imageInputRef.current.value = "";
            }
          }}
          filterType="imagen"
          title="Seleccionar imagen"
        />
        <ImageStorageSelectorDialog
          open={openImageGridSelector}
          onClose={() => setOpenImageGridSelector(false)}
          onSelect={(url) => {
            // Asegurar que la URL sea relativa (sin SERVER_URL)
            const relativeUrl = url.startsWith(SERVER_URL)
              ? url.replace(SERVER_URL + "/", "")
              : url.replace(/^\//, ""); // Quitar barra inicial si existe
            setNoticia({ ...noticia, imageGridURL: relativeUrl });
            setSelectedImageGrid(null);
            // Limpiar el input de archivo si existe
            if (imageGridInputRef.current) {
              imageGridInputRef.current.value = "";
            }
          }}
          filterType="imagen"
          title="Seleccionar imagen para grid"
        />
        <ImageStorageSelectorDialog
          open={openFileSelector}
          onClose={() => setOpenFileSelector(false)}
          onSelect={(url) => {
            // Asegurar que la URL sea relativa (sin SERVER_URL)
            const relativeUrl = url.startsWith(SERVER_URL)
              ? url.replace(SERVER_URL + "/", "")
              : url.replace(/^\//, ""); // Quitar barra inicial si existe
            setNoticia({ ...noticia, fileURL: relativeUrl });
            setSelectedFile(null);
            // Limpiar el input de archivo si existe
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          filterType="archivo"
          title="Seleccionar archivo"
        />

        {noticiaId && (
          <>
            <ConfirmDialog
              title={"Are you sure you want to delete this noticia?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteNoticia(noticiaId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "Noticia deleted successfully",
                      });
                      history.push("/noticias");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete noticia.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
